import { Loader2 } from "lucide-react";
import { STATUS_LABEL } from "../lib/api";

const STATUS_STYLE = {
  scheduled: "bg-blue-50 text-blue-800 border-blue-200", open: "bg-green-50 text-green-800 border-green-300",
  paused: "bg-amber-50 text-amber-800 border-amber-300", closed: "bg-red-50 text-red-800 border-red-200",
  finalized: "bg-stone-100 text-stone-700 border-stone-300", present: "bg-green-50 text-green-800 border-green-200",
  late: "bg-amber-50 text-amber-800 border-amber-200", pending: "bg-sky-50 text-sky-800 border-sky-200",
  pre_registered: "bg-violet-50 text-violet-800 border-violet-200", excused: "bg-stone-50 text-stone-700 border-stone-200",
  absent: "bg-stone-100 text-stone-600 border-stone-300", disputed: "bg-orange-50 text-orange-800 border-orange-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid || "status-badge"}
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${STATUS_STYLE[status] || "bg-stone-50"}`}>
    {status === "open" && <span className="h-1.5 w-1.5 rounded-full bg-green-600 animate-pulse" />}
    {STATUS_LABEL[status] || status}
  </span>
);

export const StatCard = ({ label, value, hint, icon: Icon, accent = "#C85A32", testid }) => (
  <div data-testid={testid} className="sfap-card p-5 flex flex-col gap-2 min-h-[118px]">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">{label}</span>
      {Icon && <Icon className="h-4 w-4" style={{ color: accent }} />}
    </div>
    <div className="font-serif text-3xl font-bold text-stone-900 tabular-nums" data-testid={testid ? `${testid}-value` : undefined}>
      {value ?? "—"}
    </div>
    {hint && <span className="text-xs text-stone-500">{hint}</span>}
  </div>
);

export const PageHeader = ({ title, sub, children }) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
    <div>
      <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#2D3B2D] tracking-tight">{title}</h1>
      {sub && <p className="text-sm text-stone-600 mt-1 max-w-2xl">{sub}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const Loading = ({ text = "Loading…" }) => (
  <div className="flex items-center gap-2 text-stone-500 py-10 justify-center" data-testid="loading">
    <Loader2 className="h-5 w-5 animate-spin" /> {text}
  </div>
);

export const Field = ({ label, children, hint, required }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-stone-700 mb-1.5">
      {label} {required && <span className="text-[#C85A32]">*</span>}
    </span>
    {children}
    {hint && <span className="block text-xs text-stone-500 mt-1">{hint}</span>}
  </label>
);

export const inputCls =
  "w-full h-12 rounded-xl border border-[#D9CFB8] bg-white px-4 text-base text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#2D3B2D]/30 focus:border-[#2D3B2D] transition-[border-color,box-shadow] disabled:bg-stone-100";

export const Select = ({ value, onChange, options, placeholder, testid, disabled }) => (
  <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value || null)} data-testid={testid} disabled={disabled}>
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export const Tau = ({ className = "h-8 w-8", color = "#5C3A21" }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
    <path d="M6 8h28v6H23v20h-6V14H6z" fill={color} />
  </svg>
);

export const ParishMark = ({ small }) => (
  <div className="flex items-center gap-3">
    <div className={`${small ? "h-9 w-9" : "h-11 w-11"} rounded-xl bg-[#F5F0E6] border border-[#E5DEC9] grid place-items-center`}>
      <Tau className={small ? "h-5 w-5" : "h-6 w-6"} />
    </div>
    <div className="leading-tight">
      <div className={`font-serif font-bold text-[#2D3B2D] ${small ? "text-sm" : "text-base"}`}>Saint Francis of Assisi Parish</div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-stone-500">Brgy. Mabuhay · General Santos City</div>
    </div>
  </div>
);
